import {
  FLEET_FUEL_EXCEPTION_LABELS,
  PermissionCode,
  fleetFuelCardStatusSchema,
  fleetFuelIssuerSchema,
  formatCentAsEuro,
  type FleetFuelCardStatus,
  type FleetFuelImportResult,
  type FleetFuelIssuer,
  type FleetFuelMatchStatus,
} from '@gb-ticket/shared-types';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { FleetFuelMatchBadge } from '@/products/fleet/features/fleet-fuel-match-badge';
import {
  FLEET_FUEL_CARD_STATUS_LABELS,
  FLEET_FUEL_ISSUER_LABELS,
  FLEET_FUEL_PRODUCT_LABELS,
  formatDeDateTime,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import {
  useCreateFleetFuelCard,
  useCreateFleetFuelCardAssignment,
  useFleetFuelCards,
  useFleetFuelTransactions,
  useImportFleetFuelCsv,
  useResolveFleetFuelTransaction,
  useUpdateFleetFuelCard,
} from '@/products/fleet/features/use-fleet-fuel';
import { useFleetPeople, useFleetVehicles } from '@/products/fleet/features/use-fleet';

function currentMonthYm(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${m}`;
}

export function FleetBetriebTankkartenPage() {
  usePageTitle('Betrieb — Tankkarten');
  const me = useMe();
  const fuelManage = hasPermission(me.data, PermissionCode.FLEET_FUEL_MANAGE);
  const cards = useFleetFuelCards();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('umsatz') ?? searchParams.get('umssatz');

  const [txMonth, setTxMonth] = useState(currentMonthYm);
  const [txStatus, setTxStatus] = useState<FleetFuelMatchStatus | ''>('');
  const transactions = useFleetFuelTransactions({
    month: txMonth,
    ...(txStatus ? { status: txStatus } : {}),
  });

  return (
    <div className="space-y-8">
      <PlatformPageHeader
        title="Tankkarten"
        description="Karten, Zuordnungen, Import und Umsätze."
      />
      <FleetFuelCardsSection manage={fuelManage} cards={cards} />
      {fuelManage ? <FleetFuelImportSection /> : null}
      <FleetFuelTransactionsSection
        manage={fuelManage}
        month={txMonth}
        onMonth={setTxMonth}
        status={txStatus}
        onStatus={setTxStatus}
        transactions={transactions}
        highlightId={highlightId}
      />
    </div>
  );
}

const FUEL_ISSUERS = fleetFuelIssuerSchema.options;
const CARD_STATUSES = fleetFuelCardStatusSchema.options;

function FleetFuelCardsSection({
  manage,
  cards,
}: {
  manage: boolean;
  cards: ReturnType<typeof useFleetFuelCards>;
}) {
  const create = useCreateFleetFuelCard();
  const update = useUpdateFleetFuelCard();
  const assign = useCreateFleetFuelCardAssignment();
  const vehicles = useFleetVehicles(false);
  const [peopleQuery, setPeopleQuery] = useState('');
  const people = useFleetPeople(peopleQuery, { minLength: 0, enabled: manage });

  const [issuer, setIssuer] = useState<FleetFuelIssuer>('dkv');
  const [externalRef, setExternalRef] = useState('');
  const [last4, setLast4] = useState('');
  const [label, setLabel] = useState('');

  const [assignCardId, setAssignCardId] = useState('');
  const [assignVehicleId, setAssignVehicleId] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  const [assignFrom, setAssignFrom] = useState(new Date().toISOString().slice(0, 10));
  const [assignTo, setAssignTo] = useState('');

  async function onCreateCard(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        issuer,
        externalRef: externalRef.trim(),
        last4: last4.trim(),
        label: label.trim() ? label.trim() : null,
      });
      setExternalRef('');
      setLast4('');
      setLabel('');
      toast.success('Tankkarte angelegt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Karte konnte nicht angelegt werden.',
      );
    }
  }

  async function onAssign(event: FormEvent) {
    event.preventDefault();
    if (!assignCardId) {
      return;
    }
    try {
      await assign.mutateAsync({
        cardId: assignCardId,
        body: {
          vehicleId: assignVehicleId || null,
          userId: assignUserId || null,
          validFrom: assignFrom,
          validTo: assignTo || null,
        },
      });
      toast.success('Zuordnung gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Zuordnung konnte nicht gespeichert werden.',
      );
    }
  }

  const items = cards.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tankkarten</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {items.length === 0 && !cards.isPending ? (
          <p className="text-sm text-muted-foreground">Noch keine Tankkarte angelegt.</p>
        ) : null}
        {items.length > 0 ? (
          <>
            <div className={`hidden md:block ${fleetTableFrameClassName}`}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aussteller</TableHead>
                    <TableHead>Referenz</TableHead>
                    <TableHead>Endziffern</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Bezeichnung</TableHead>
                    {manage ? <TableHead>Aktion</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((card) => (
                    <TableRow key={card.id}>
                      <TableCell>{FLEET_FUEL_ISSUER_LABELS[card.issuer]}</TableCell>
                      <TableCell className="font-mono text-xs">{card.externalRef}</TableCell>
                      <TableCell>{card.last4}</TableCell>
                      <TableCell>
                        {manage ? (
                          <Select
                            value={card.status}
                            onValueChange={(value) => {
                              const status = value as FleetFuelCardStatus;
                              void update
                                .mutateAsync({ cardId: card.id, body: { status } })
                                .then(() => toast.success('Status gespeichert.'))
                                .catch((error: unknown) => {
                                  toast.error(
                                    error instanceof ApiRequestError
                                      ? error.message
                                      : 'Status konnte nicht gespeichert werden.',
                                  );
                                });
                            }}
                          >
                            <SelectTrigger
                              aria-label="Kartenstatus"
                              className="h-9 w-full min-w-[8rem]"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CARD_STATUSES.map((value) => (
                                <SelectItem key={value} value={value}>
                                  {FLEET_FUEL_CARD_STATUS_LABELS[value]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          FLEET_FUEL_CARD_STATUS_LABELS[card.status]
                        )}
                      </TableCell>
                      <TableCell>
                        {manage ? (
                          <Input
                            className="min-w-32"
                            defaultValue={card.label ?? ''}
                            onBlur={(event) => {
                              const next = event.target.value.trim();
                              if (next === (card.label ?? '')) {
                                return;
                              }
                              void update.mutateAsync({
                                cardId: card.id,
                                body: { label: next ? next : null },
                              });
                            }}
                          />
                        ) : (
                          (card.label ?? '—')
                        )}
                      </TableCell>
                      {manage ? (
                        <TableCell>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setAssignCardId(card.id)}
                          >
                            Zuordnen
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <ul className={`divide-y md:hidden ${fleetTableFrameClassName}`}>
              {items.map((card) => (
                <li key={card.id} className="space-y-2 p-3">
                  <p className="font-medium">
                    {FLEET_FUEL_ISSUER_LABELS[card.issuer]} · {card.last4}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">{card.externalRef}</p>
                  <p className="text-sm">{FLEET_FUEL_CARD_STATUS_LABELS[card.status]}</p>
                  {card.label ? (
                    <p className="text-sm text-muted-foreground">{card.label}</p>
                  ) : null}
                  {manage ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="min-h-11"
                      onClick={() => setAssignCardId(card.id)}
                    >
                      Zuordnen
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {manage ? (
          <>
            <form
              className={`${fleetPanelClassName} grid gap-4 sm:grid-cols-2`}
              onSubmit={(event) => void onCreateCard(event)}
            >
              <div>
                <Label htmlFor="card-issuer">Aussteller</Label>
                <Select
                  value={issuer}
                  onValueChange={(value) => setIssuer(value as FleetFuelIssuer)}
                >
                  <SelectTrigger id="card-issuer" className="mt-2 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_ISSUERS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {FLEET_FUEL_ISSUER_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="card-ref">Referenz</Label>
                <Input
                  id="card-ref"
                  className="mt-2"
                  value={externalRef}
                  onChange={(event) => setExternalRef(event.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="card-last4">Endziffern</Label>
                <Input
                  id="card-last4"
                  className="mt-2"
                  inputMode="numeric"
                  maxLength={4}
                  value={last4}
                  onChange={(event) => setLast4(event.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="card-label">Bezeichnung (optional)</Label>
                <Input
                  id="card-label"
                  className="mt-2"
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={create.isPending}>
                  Tankkarte anlegen
                </Button>
              </div>
            </form>

            <form
              className={`${fleetPanelClassName} space-y-4`}
              onSubmit={(event) => void onAssign(event)}
            >
              <p className="text-sm font-medium">Zuordnung</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="assign-card">Karte</Label>
                  <Select
                    value={assignCardId || 'none'}
                    onValueChange={(value) => setAssignCardId(value === 'none' ? '' : value)}
                  >
                    <SelectTrigger id="assign-card" className="mt-2 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Bitte wählen …</SelectItem>
                      {items.map((card) => (
                        <SelectItem key={card.id} value={card.id}>
                          {FLEET_FUEL_ISSUER_LABELS[card.issuer]} · {card.externalRef} ·{' '}
                          {card.last4}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assign-vehicle">Fahrzeug (optional)</Label>
                  <Select
                    value={assignVehicleId || 'none'}
                    onValueChange={(value) => setAssignVehicleId(value === 'none' ? '' : value)}
                  >
                    <SelectTrigger id="assign-vehicle" className="mt-2 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {(vehicles.data?.items ?? []).map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.licensePlate}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assign-person">Person (optional)</Label>
                  <Input
                    id="assign-person-search"
                    className="mt-2 mb-2"
                    placeholder="Suchen …"
                    value={peopleQuery}
                    onChange={(event) => setPeopleQuery(event.target.value)}
                  />
                  <Select
                    value={assignUserId || 'none'}
                    onValueChange={(value) => setAssignUserId(value === 'none' ? '' : value)}
                  >
                    <SelectTrigger id="assign-person" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {(people.data?.items ?? []).map((person) => (
                        <SelectItem key={person.id} value={person.id}>
                          {person.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assign-from">Von</Label>
                  <Input
                    id="assign-from"
                    className="mt-2"
                    type="date"
                    value={assignFrom}
                    onChange={(event) => setAssignFrom(event.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="assign-to">Bis (optional)</Label>
                  <Input
                    id="assign-to"
                    className="mt-2"
                    type="date"
                    value={assignTo}
                    onChange={(event) => setAssignTo(event.target.value)}
                  />
                </div>
              </div>
              <Button type="submit" disabled={assign.isPending}>
                Zuordnung speichern
              </Button>
            </form>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function FleetFuelImportSection() {
  const importCsv = useImportFleetFuelCsv();
  const [issuer, setIssuer] = useState<FleetFuelIssuer>('dkv');
  const [result, setResult] = useState<FleetFuelImportResult | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>CSV-Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs">
          <Label htmlFor="import-issuer">Aussteller</Label>
          <Select value={issuer} onValueChange={(value) => setIssuer(value as FleetFuelIssuer)}>
            <SelectTrigger id="import-issuer" className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FUEL_ISSUERS.map((value) => (
                <SelectItem key={value} value={value}>
                  {FLEET_FUEL_ISSUER_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="fuel-csv">Datei</Label>
          <Input
            id="fuel-csv"
            className="mt-2"
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) {
                return;
              }
              void importCsv
                .mutateAsync({ issuer, file })
                .then((res) => {
                  setResult(res);
                  toast.success('Import abgeschlossen.');
                })
                .catch((error: unknown) => {
                  toast.error(
                    error instanceof ApiRequestError
                      ? error.message
                      : 'Der Import ist fehlgeschlagen.',
                  );
                });
              event.target.value = '';
            }}
          />
        </div>
        {result ? (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">Neu</dt>
              <dd>{result.created}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Duplikat</dt>
              <dd>{result.duplicated}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Ausnahmen</dt>
              <dd>{result.exceptions}</dd>
            </div>
            {result.rowErrors.length > 0 ? (
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">Zeilenfehler</dt>
                <dd>
                  <ul className="mt-1 list-disc pl-5">
                    {result.rowErrors.map((row) => (
                      <li key={`${row.line}-${row.message}`}>
                        Zeile {row.line}: {row.message}
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </CardContent>
    </Card>
  );
}

function FleetFuelTransactionsSection({
  manage,
  month,
  onMonth,
  status,
  onStatus,
  transactions,
  highlightId,
}: {
  manage: boolean;
  month: string;
  onMonth: (value: string) => void;
  status: FleetFuelMatchStatus | '';
  onStatus: (value: FleetFuelMatchStatus | '') => void;
  transactions: ReturnType<typeof useFleetFuelTransactions>;
  highlightId: string | null;
}) {
  const resolve = useResolveFleetFuelTransaction();
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  const items = transactions.data?.items ?? [];

  useEffect(() => {
    if (!highlightId) {
      return;
    }
    const row = rowRefs.current.get(highlightId);
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, items.length]);

  async function onResolve(event: FormEvent) {
    event.preventDefault();
    if (!resolveId) {
      return;
    }
    try {
      await resolve.mutateAsync({ transactionId: resolveId, body: { note: resolveNote.trim() } });
      setResolveId(null);
      setResolveNote('');
      toast.success('Ausnahme aufgelöst.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Ausnahme konnte nicht aufgelöst werden.',
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Umsätze</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="max-w-xs flex-1">
            <Label htmlFor="tx-month">Monat</Label>
            <Input
              id="tx-month"
              className="mt-2"
              type="month"
              value={month}
              onChange={(event) => onMonth(event.target.value)}
            />
          </div>
          <div className="max-w-xs flex-1">
            <Label htmlFor="tx-status">Status</Label>
            <Select
              value={status || 'none'}
              onValueChange={(value) =>
                onStatus(value === 'none' ? '' : (value as FleetFuelMatchStatus))
              }
            >
              <SelectTrigger id="tx-status" className="mt-2 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Alle</SelectItem>
                <SelectItem value="matched">Zugeordnet</SelectItem>
                <SelectItem value="exception">Ausnahme</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {items.length === 0 && !transactions.isPending ? (
          <p className="text-sm text-muted-foreground">Keine Umsätze in diesem Filter.</p>
        ) : null}
        {items.length > 0 ? (
          <>
            <div className={`hidden md:block ${fleetTableFrameClassName}`}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zeitpunkt</TableHead>
                    <TableHead>Referenz</TableHead>
                    <TableHead>Produkt</TableHead>
                    <TableHead>Betrag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Grund</TableHead>
                    {manage ? <TableHead /> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((tx) => {
                    const highlighted = highlightId === tx.id;
                    const canResolve =
                      manage &&
                      tx.status === 'exception' &&
                      tx.resolvedAt == null &&
                      resolveId !== tx.id;
                    return (
                      <TableRow
                        key={tx.id}
                        ref={(node) => {
                          if (node) {
                            rowRefs.current.set(tx.id, node);
                          }
                        }}
                        className={highlighted ? 'bg-muted/60' : undefined}
                      >
                        <TableCell>{formatDeDateTime(tx.occurredAt)}</TableCell>
                        <TableCell className="font-mono text-xs">{tx.externalId}</TableCell>
                        <TableCell>{FLEET_FUEL_PRODUCT_LABELS[tx.product]}</TableCell>
                        <TableCell>{formatCentAsEuro(tx.amountCents)}</TableCell>
                        <TableCell>
                          <FleetFuelMatchBadge status={tx.status} />
                        </TableCell>
                        <TableCell>
                          {tx.status === 'exception' && tx.exceptionCode ? (
                            <span>
                              {FLEET_FUEL_EXCEPTION_LABELS[tx.exceptionCode] ?? tx.exceptionCode}
                            </span>
                          ) : (
                            '—'
                          )}
                          {tx.resolvedAt ? (
                            <Badge variant="outline" className="ml-2">
                              Aufgelöst
                            </Badge>
                          ) : null}
                        </TableCell>
                        {manage ? (
                          <TableCell>
                            {canResolve ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setResolveId(tx.id)}
                              >
                                Auflösen
                              </Button>
                            ) : null}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <ul className={`divide-y md:hidden ${fleetTableFrameClassName}`}>
              {items.map((tx) => {
                const canResolve =
                  manage &&
                  tx.status === 'exception' &&
                  tx.resolvedAt == null &&
                  resolveId !== tx.id;
                return (
                  <li key={tx.id} className="space-y-2 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium">{formatCentAsEuro(tx.amountCents)}</p>
                      <FleetFuelMatchBadge status={tx.status} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatDeDateTime(tx.occurredAt)}
                    </p>
                    <p className="font-mono text-xs">{tx.externalId}</p>
                    <p className="text-sm">{FLEET_FUEL_PRODUCT_LABELS[tx.product]}</p>
                    {tx.status === 'exception' && tx.exceptionCode ? (
                      <p className="text-sm">
                        {FLEET_FUEL_EXCEPTION_LABELS[tx.exceptionCode] ?? tx.exceptionCode}
                      </p>
                    ) : null}
                    {canResolve ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="min-h-11"
                        onClick={() => setResolveId(tx.id)}
                      >
                        Auflösen
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}
        {resolveId ? (
          <form
            className={`${fleetPanelClassName} space-y-3`}
            onSubmit={(event) => void onResolve(event)}
          >
            <Label htmlFor="resolve-note">Notiz (mindestens 10 Zeichen)</Label>
            <Input
              id="resolve-note"
              value={resolveNote}
              onChange={(event) => setResolveNote(event.target.value)}
              required
              minLength={10}
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={resolve.isPending || resolveNote.trim().length < 10}>
                Auflösung speichern
              </Button>
              <Button type="button" variant="ghost" onClick={() => setResolveId(null)}>
                Abbrechen
              </Button>
            </div>
          </form>
        ) : null}
      </CardContent>
    </Card>
  );
}
